import classNames from 'classnames';
import { inject, observer } from 'mobx-react';
import PropTypes from 'prop-types';
import React, { Fragment, useState } from 'react';
import Skeleton from 'react-loading-skeleton';

import FullProduct from '../../../models/product/FullProduct';
import FullProductMatrix from '../../../models/product/FullProductMatrix';
import { modelOf } from '../../../prop-types';
import ConfigStore from '../../../store/ConfigStore';
import UIStore from '../../../store/UIStore';
import RequestState from '../../../types/RequestState';
import { blockTestRender } from '../../../util/process';
import Icon from '../../common/Icon';
import ProductCollectionMatrixQuantityCell from '../ProductCollectionMatrixQuantityCell';
import ProductCollectionMatrixStorageRow from '../ProductCollectionMatrixStorageRow';
import ProductCollectionMatrixStorageRowSkeleton from '../ProductCollectionMatrixStorageRowSkeleton';
import ProductImage from '../ProductImage';

const ProductCollectionMatrixRow = ({
  element,
  product,
  productMatrix,
  uiStore,
  configStore,
  ifProductRowImages,
  tooltipStates,
  onImageClick,
  onQuantityChange,
  isLeftAligned,
}) => {
  const [imageLoaded, setImageLoaded] = useState(false);

  const setMainImageIndex = (productRowImage) => {
    const mainImageIndex = product.images.findIndex(
      (image) => image.for_color_id === productRowImage.for_color_id
    );
    onImageClick(mainImageIndex);
  };

  const findProductCollectionElementItem = (
    productCollectionColumnElementId,
    productCollectionRowElementId
  ) =>
    productMatrix.getItem(
      productCollectionColumnElementId,
      productCollectionRowElementId
    );

  const findRowElementById = (rowElementId) =>
    productMatrix
      .getRowElements()
      .find((rowElement) => rowElement.id === rowElementId);

  const renderProductQuantityInputs = (productCollectionElementId) => {
    const rowElements = productMatrix.getRowElements();

    return (
      rowElements &&
      rowElements.map((rowElement) => {
        const elementIds = productMatrix.shouldSwapCollectionOrder()
          ? { rowId: productCollectionElementId, columnId: rowElement.id }
          : { columnId: productCollectionElementId, rowId: rowElement.id };

        const productCollectionElementItem = findProductCollectionElementItem(
          elementIds.columnId,
          elementIds.rowId
        );

        const element = uiStore.isMobile
          ? findRowElementById(rowElement.id)
          : null;

        const quantityKey = `qty--${productCollectionElementItem.row_id}--${productCollectionElementItem.column_id}`;
        const tooltipKey = `${productCollectionElementItem.row_id}--${productCollectionElementItem.column_id}--tooltip`;

        return (
          <ProductCollectionMatrixQuantityCell
            key={quantityKey}
            productCollectionElementItem={productCollectionElementItem}
            product={product}
            productMatrix={productMatrix}
            element={element}
            isTooltipOpen={tooltipStates[tooltipKey]}
            onQuantityChange={(event) =>
              onQuantityChange(event, productCollectionElementItem)
            }
            isLeftAligned={productMatrix.isLeftAligned}
          />
        );
      })
    );
  };

  const productRowImage = product.images.find(
    (image) => image.for_color_id === element.id
  );
  const columnTitle = productMatrix.getNames();
  ifProductRowImages && ifProductRowImages(productRowImage);

  const handleImageLoad = () => {
    setImageLoaded(true);
  };

  const handleImageLoadError = () => {
    setImageLoaded(true);
  };

  const productImage = (
    <div className="ProductCollectionMatrixRow__image-wrapper">
      {!imageLoaded && blockTestRender() && (
        <div className="ProductCollectionMatrixRow__image-skeleton">
          <Skeleton height={100} width={100} />
        </div>
      )}
      <div style={{ display: imageLoaded ? 'block' : 'none' }}>
        <ProductImage
          product={product}
          size={'small'}
          productImage={productRowImage}
          forceAspectRatio={false}
          lazyLoading={false}
          overlayIcon={
            productRowImage && (
              <span className="ProductCollectionMatrixRow__image-overlay-icon">
                <Icon name="expand" />
              </span>
            )
          }
        />
        <img
          src={
            productRowImage
              ? productRowImage.sizes['small']
              : '/img/no_image_available.png'
          }
          alt=""
          onLoad={handleImageLoad}
          onError={handleImageLoadError}
          style={{ display: 'none' }}
        />
      </div>
      <p className="ProductCollectionMatrixRow__product-property">
        {columnTitle.columnName}: {element.name}
      </p>
    </div>
  );

  const shouldShowStorageRows =
    configStore.productPage.matrixDetailedStockInfoEnabled &&
    productMatrix.stocksInfoState === RequestState.LOADED;

  const isLoadingStorageRows =
    configStore.productPage.matrixDetailedStockInfoEnabled &&
    productMatrix.stocksInfoState === RequestState.LOADING;

  const getUniqueStoragesForRow = () => {
    if (!shouldShowStorageRows) return [];

    const storageMap = new Map();
    const rowElements = productMatrix.getRowElements();

    rowElements.forEach((rowElement) => {
      const elementIds = productMatrix.shouldSwapCollectionOrder()
        ? { rowId: element.id, columnId: rowElement.id }
        : { columnId: element.id, rowId: rowElement.id };

      const item = productMatrix.getItem(elementIds.columnId, elementIds.rowId);

      if (item) {
        const stockData = item.stocks.get(item.product.id);
        if (stockData && stockData.storages) {
          Array.from(stockData.storages.entries()).forEach(
            ([storageId, storage]) => {
              if (!storageMap.has(storageId)) {
                storageMap.set(storageId, {
                  id: storageId,
                  name: storage.translations.storage_name,
                });
              }
            }
          );
        }
      }
    });

    return Array.from(storageMap.values());
  };

  const renderStorageRows = () => {
    if (!blockTestRender()) {
      return null;
    }

    if (isLoadingStorageRows) {
      return [0, 1].map((index) => (
        <ProductCollectionMatrixStorageRowSkeleton
          key={`skeleton-${element.id}-${index}`}
          productMatrix={productMatrix}
        />
      ));
    }

    if (!shouldShowStorageRows) return null;

    const storages = getUniqueStoragesForRow();

    return storages.map((storage) => (
      <ProductCollectionMatrixStorageRow
        key={`storage-${storage.id}-${element.id}`}
        element={element}
        productMatrix={productMatrix}
        storageInfo={storage}
        isLeftAligned={isLeftAligned}
      />
    ));
  };

  return (
    <Fragment key={element.id}>
      <tr
        className={classNames('ProductCollectionMatrixRow', {
          'ProductCollectionMatrixRow__detailed-stocks-info':
            configStore.productPage.matrixDetailedStockInfoEnabled,
        })}
      >
        {!uiStore.isMobile && (
          <th
            className="ProductCollectionMatrixRow__header"
            scope="row"
            onClick={
              productRowImage && (() => setMainImageIndex(productRowImage))
            }
          >
            {productImage}
          </th>
        )}
        {uiStore.isMobile && (
          <td
            className="ProductCollectionMatrixRow__label"
            onClick={
              productRowImage && (() => setMainImageIndex(productRowImage))
            }
          >
            {productImage}
          </td>
        )}
        {element && renderProductQuantityInputs(element.id)}
      </tr>
      {renderStorageRows()}
    </Fragment>
  );
};

ProductCollectionMatrixRow.propTypes = {
  element: PropTypes.object.isRequired,
  product: modelOf(FullProduct).isRequired,
  productMatrix: modelOf(FullProductMatrix).isRequired,
  uiStore: modelOf(UIStore).isRequired,
  configStore: modelOf(ConfigStore).isRequired,
  tooltipStates: PropTypes.object.isRequired,
  onImageClick: PropTypes.func.isRequired,
  onQuantityChange: PropTypes.func.isRequired,
  ifProductRowImages: PropTypes.func,
  isLeftAligned: PropTypes.bool,
};

export default inject(
  'uiStore',
  'configStore'
)(observer(ProductCollectionMatrixRow));
